import { Caption } from "@design-system/shadcn";
import React, { useMemo } from "react";
import { z } from "zod";

import { useFieldContext } from "../core/form-contexts";

type FormFieldCaptionProps = Omit<
  React.ComponentProps<typeof Caption>,
  "children"
> & {
  children?: React.ReactNode;
};

// Helper function to extract error messages from zod error objects or strings
const extractErrorMessage = (error: string | z.ZodIssue): string => {
  if (typeof error === "string") {
    return error;
  }
  // ZodIssue has a message property
  return error.message;
};

function FormFieldCaption(props: FormFieldCaptionProps) {
  const field = useFieldContext<string>();

  if (!field) {
    throw new Error("FormFieldCaption must be used within form.AppField");
  }

  const hasError = useMemo(
    () => field.state.meta.errors.length > 0,
    [field.state.meta.errors.length]
  );

  const errorMessages = useMemo(
    () => field.state.meta.errors.map(extractErrorMessage),
    [field.state.meta.errors]
  );

  if (!hasError || errorMessages.length === 0) {
    return null;
  }

  return (
    <Caption status="error" {...props}>
      {errorMessages.join(", ")}
    </Caption>
  );
}

export { FormFieldCaption };
export type { FormFieldCaptionProps };
